import { Flex, Input, notification, Popover, Radio, Spin, Switch, Tooltip, Typography } from "antd";
import React, { useEffect, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import { InfoCircleOutlined, MailOutlined, SettingOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { updateOrganization } from "../../redux/organizationSlice";
import CustomCssTransition from "../UI/CustomCssTransition/customCssTransition";
import SaveButton from "../UI/savebutton";
import ProvidersTags from "../ProvidersTags";
import Editor from "../Editor";

const { Title } = Typography;

export default function MarketingSettings() {
  const [isDirty, setIsDirty] = useState(false);
  const [enhanceLeads, setEnhanceLeads] = useState(false);
  const [isThankYouPage, setIsThankYouPage] = useState({
    thankyou_page: false,
    thankyou_page_url: "",
  });
  const [smsSettings, setSmsSettings] = useState({
    compliance: true,
    checkboxOption: true,
    contactUnchecked: false,
    addTag: false,
    tagIds: [],
    consentNote: "",
    consentSubtext: ""
  });
  const [validationErrors, setValidationErrors] = useState({
    consentNote: false,
    consentSubtext: false,
    tagIds: false
  });
  const [editorContent, setEditorContent] = useState("");

  const { organizationData, loading } = useSelector(
    (state) => state?.organization
  );
  const { isAdmin } = useSelector((state) => state?.auth);
  const dispatch = useDispatch();
  const isHCP = organizationData?.organization_provider === 'HCP';

  useEffect(() => {
    setEnhanceLeads(organizationData?.enhanceLeads ?? false);
    setIsThankYouPage({
      thankyou_page:
        organizationData?.thankyou_page_config?.thankyou_page ?? false,
      thankyou_page_url:
        organizationData?.thankyou_page_config?.thankyou_page_url ?? "",
    });
    const smsComplianceData = organizationData?.sms_compliance;
    setSmsSettings({
      compliance: smsComplianceData?.enabled ?? true,
      checkboxOption: typeof smsComplianceData?.checkbox_precheck === 'boolean' 
        ? smsComplianceData.checkbox_precheck 
        : (smsComplianceData?.checkbox_precheck ?? "yes") === "yes",
      contactUnchecked: typeof smsComplianceData?.contact_unchecked === 'boolean'
        ? smsComplianceData.contact_unchecked
        : (smsComplianceData?.contact_unchecked ?? "no") === "yes",
      addTag: typeof smsComplianceData?.add_tag === 'boolean'
        ? smsComplianceData.add_tag
        : (smsComplianceData?.add_tag ?? "no") === "yes",
      tagIds: smsComplianceData?.tag_ids || [],
      consentNote: smsComplianceData?.consent_note || "",
      consentSubtext: smsComplianceData?.consent_subtext || ""
    });
    setEditorContent(organizationData?.editorContent || "We may contact you by phone, email, or SMS about this visit.");
  }, [
    organizationData?.enhanceLeads,
    organizationData?.thankyou_page_config?.thankyou_page,
    organizationData?.thankyou_page_config?.thankyou_page_url,
    organizationData?.sms_compliance,
    organizationData?.editorContent,
  ]);

  const handleSwitchChange = (checked) => {
    setIsThankYouPage({
      ...isThankYouPage,
      thankyou_page: checked,
    });
    setIsDirty(true);
  };


  const handleSubmit = () => {
    if (
      isThankYouPage.thankyou_page &&
      isThankYouPage.thankyou_page_url === ""
    ) {
      notification.error({
        message: "Error",
        description: "Please enter the Thank You page URL",
        duration: 3,
      });
      return;
    }

    // Reset validation errors
    setValidationErrors({
      consentNote: false,
      consentSubtext: false,
      tagIds: false
    });

    let hasErrors = false;
    const newErrors = {
      consentNote: false,
      consentSubtext: false,
      tagIds: false
    };

    if (!smsSettings.compliance && smsSettings.consentNote.trim() === "") {
      newErrors.consentNote = true;
      hasErrors = true;
    }
    if (!smsSettings.compliance && smsSettings.addTag && smsSettings.tagIds.length === 0) {
      newErrors.tagIds = true;
      hasErrors = true;
    }

    if (hasErrors) {
      setValidationErrors(newErrors);
      notification.error({
        message: "Validation Error",
        description: "Please fill in all required fields",
        duration: 3,
      });
      return;
    }

    const smsComplianceConfig = {
      enabled: smsSettings.compliance,
      checkbox_precheck: smsSettings.checkboxOption,
      contact_unchecked: smsSettings.contactUnchecked,
      add_tag: smsSettings.addTag,
      tag_ids: smsSettings.tagIds,
      consent_note: smsSettings.consentNote,
      consent_subtext: smsSettings.consentSubtext,
    };

    dispatch(
      updateOrganization({
        id: organizationData._id,
        payload: { 
          ...organizationData, 
          enhanceLeads,
          thankyou_page_config: isThankYouPage,
          sms_compliance: smsComplianceConfig,
          editorContent: editorContent
        },
        page: "Marketing Settings"
      })
    );
    setIsDirty(false);
  };
  return (
    <div>
      <Spin spinning={loading} fullscreen />
      <Flex align="center" gap={14} style={{ marginBottom: "20px" }}>
        <SettingOutlined style={{ fontSize: 24 }} />
        <Title level={3} style={{ margin: 0 }}>
          Marketing Settings
        </Title>
      </Flex>

      <div className="bg-white shadow-md rounded-lg p-6 mb-6">
        <label className="text-base text-black font-semibold">
          Custom Thank You Page
        </label>
        <Tooltip title={null}>
          <Switch
          className="ml-4"
          checked={isThankYouPage?.thankyou_page ?? false}
          onChange={handleSwitchChange}
        />
        </Tooltip>
        <CustomCssTransition
          in={isThankYouPage?.thankyou_page}
          timeout={300}
          classNames="glimpse"
          unmountOnExit
        >
          <div className="flex flex-col justify-start gap-4 mt-4">
            <label className="text-base text-black font-semibold">
              Thank You Page URL
            </label>
            <Input
              placeholder="Enter the Thank You page URL"
              className="profile_input_wrapper"
              style={{ width: "80%" }}
              value={isThankYouPage?.thankyou_page_url}
              onChange={(e) => {
                setIsThankYouPage({
                  thankyou_page: isThankYouPage?.thankyou_page,
                  thankyou_page_url: e.target.value,
                });
              }}
              allowClear
            />
          </div>
        </CustomCssTransition>
      </div>

      <div className="bg-white shadow-md rounded-lg p-6 mb-6">
        <div className="mb-4 flex items-center gap-2">
          <label className="text-lg text-black font-semibold">
            📱 SMS Compliance
          </label>
          <Popover
            content={
              <div style={{ maxWidth: '280px' }}>
                <p><strong>SMS compliance settings determine how customer consent for text messaging is collected and managed.</strong></p>
                <br/>
                <p>Choose <strong>Disclaimer Only</strong> to show only your disclaimer text from Client Additional Note inside <strong>Company Profile Settings</strong>, or select <strong>Opt-in Based Consent</strong> for advanced compliance options.</p>
              </div>
            }
            title="SMS Compliance Info"
            trigger="hover"
          >
            <InfoCircleOutlined style={{ color: '#1890ff', cursor: 'pointer' }} />
          </Popover>
        </div>

        <div className="flex flex-col gap-4">
          <label className="text-base text-black font-semibold">
            How would you like to handle SMS consent?
          </label>
          <Radio.Group
            value={smsSettings.compliance ? "disclaimer_only" : "checkbox_consent"}
            onChange={(e) => {
              setSmsSettings(prev => ({...prev, compliance: e.target.value === "disclaimer_only"}));
              setIsDirty(true);
            }}
          >
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-3">
                <Radio value="disclaimer_only" className="text-sm">Disclaimer Only</Radio>
              </div>
              <div className="flex items-center gap-3">
                <Radio value="checkbox_consent" className="text-sm">Opt-in Based Consent</Radio>
              </div>
            </div>
          </Radio.Group>
        </div>
      </div>

      <CustomCssTransition
        in={!smsSettings.compliance}
        timeout={300}
        classNames="glimpse"
        unmountOnExit
      >
        <div className="flex flex-col justify-start gap-6 bg-white shadow-md rounded-lg p-6 mb-6">
          <div className="flex flex-col gap-4">
            <label className="text-base text-black font-semibold">
              💬 SMS consent opt-in by default
            </label>
            <div className="flex items-center gap-3">
              <Switch
                checked={smsSettings.checkboxOption}
                onChange={(checked) => {
                  setSmsSettings(prev => ({...prev, checkboxOption: checked}));
                  setIsDirty(true);
                }}
              />
              <span className="text-sm text-gray-500">
                {smsSettings.checkboxOption 
                  ? "- The consent checkbox will be pre-checked for customers" 
                  : "- The consent checkbox will be unchecked by default"
                }
              </span>
            </div>
          </div>

          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-2">
              <label className="text-base text-black font-semibold">
                📝 Consent Message
              </label>
              <Popover
                content={<div style={{ maxWidth: '280px' }}>Enter the message that will be displayed with the SMS consent opt-in option on the scheduler</div>}
                title="Consent Message"
                trigger="hover"
              >
                <InfoCircleOutlined style={{ color: '#1890ff', cursor: 'pointer' }} />
              </Popover>
            </div>
            <Input.TextArea
              placeholder="e.g., 'I agree to receive text messages for appointment updates and service reminders'"
              value={smsSettings.consentNote}
              onChange={(e) => {
                setSmsSettings(prev => ({...prev, consentNote: e.target.value}));
                setIsDirty(true);
                if (validationErrors.consentNote) {
                  setValidationErrors(prev => ({...prev, consentNote: false}));
                }
              }}
              maxLength={150}
              showCount
              autoSize={{
                minRows: 2,
                maxRows: 4,
              }}
              style={{ 
                width: "100%",
                borderColor: validationErrors.consentNote ? '#ff4d4f' : undefined
              }}
            />
            {validationErrors.consentNote && (
              <p className="text-sm text-red-500 mt-1">Consent Message is required</p>
            )}
          </div>

          <div className="flex flex-col gap-4 ml-6 pl-4 border-l-2 border-gray-200 bg-gray-50 rounded-r-lg p-4">
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600 font-medium">
                ↳ Additional Terms (Optional)
              </label>
              <Popover
                content={<div style={{ maxWidth: '280px' }}>Additional legal terms or fine print that will appear below the main consent message</div>}
                title="Additional Terms"
                trigger="hover"
              >
                <InfoCircleOutlined style={{ color: '#1890ff', cursor: 'pointer', fontSize: '12px' }} />
              </Popover>
            </div>
            <div style={{ position: 'relative' }}>
                <ReactQuill
                value={smsSettings.consentSubtext}
                onChange={(value) => {
                  const textContent = value.replace(/<[^>]*>/g, '');
                  if (textContent.length <= 200) {
                    setSmsSettings(prev => ({...prev, consentSubtext: value}));
                    setIsDirty(true);
                    if (validationErrors.consentSubtext) {
                      setValidationErrors(prev => ({...prev, consentSubtext: false}));
                    }
                  }
                }}
                onKeyPress={(event) => {
                  const textContent = smsSettings.consentSubtext.replace(/<[^>]*>/g, '');
                  if (textContent.length >= 200) {
                    event.preventDefault();
                  }
                }}
                modules={{
                  toolbar: [
                    ['bold', 'italic', 'underline'],
                    ['link'],
                    ['clean']
                  ]
                }}
                formats={['bold', 'italic', 'underline', 'list', 'bullet', 'link']}
                style={{ 
                  fontSize: '14px',
                  maxHeight: '200px'
                }}
              />
              <style jsx global>{`
                .ql-editor {
                  color: #000000 !important;
                  font-size: 14px !important;
                  max-height: 150px !important;
                  overflow-y: auto !important;
                }
                .ql-editor p {
                  color: #000000 !important;
                }
                .ql-container {
                  overflow: visible !important;
                }
                .ql-tooltip {
                  z-index: 10000 !important;
                  position: absolute !important;
                  transform: translateY(0) !important;
                }
                .ql-editing {
                  z-index: 10001 !important;
                  position: absolute !important;
                }
                .ql-picker-options {
                  z-index: 10002 !important;
                  position: absolute !important;
                }
              `}</style>
              <div className={`text-sm text-right ${(() => {
              const textContent = smsSettings.consentSubtext.replace(/<[^>]*>/g, '');
              return textContent.length > 200 ? 'text-red-500 font-semibold' : 'text-gray-500';
            })()}`}>
              {(() => {
                const textContent = smsSettings.consentSubtext.replace(/<[^>]*>/g, '');
                return textContent.length;
              })()}/200
              </div>
            </div>
          </div>

        </div>
      </CustomCssTransition>

      <CustomCssTransition
        in={!smsSettings.compliance}
        timeout={300}
        classNames="glimpse"
        unmountOnExit
      >
        <div className="bg-white shadow-md rounded-lg p-6 mb-6">
          <div className="mb-4 flex items-center gap-2">
            <label className="text-lg text-black font-semibold">
              ❓ What action should be taken in {isHCP ? 'HouseCallPro' : 'ServiceTitan'} when customers opt out of SMS consent?
            </label>
            <Popover
              content={
                <div style={{ maxWidth: '280px' }}>
                  <p><strong>Important:</strong> Choose how to handle customers who <strong>opt out</strong> of SMS consent - this affects their marketing preferences and contact settings.</p>
                </div>
              }
              title="Opt-Out Consent Action"
              trigger="hover"
            >
              <InfoCircleOutlined style={{ color: '#1890ff', cursor: 'pointer' }} />
            </Popover>
          </div>


          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-2">
                <label className="text-base text-black font-semibold">
                  <MailOutlined/> {isHCP ? 'Disable Notifications' : 'Mark as Do Not Mail'}
                </label>
                <Popover
                  content={
                    <div style={{ maxWidth: '280px' }}>
                      <p>When customers opt out of SMS consent, would you like us to {isHCP ? 'disable notifications' : 'mark them as "do not mail"'}?</p>
                      <br/>
                      <p><strong>Integration Info:</strong> SMS compliance settings will sync with {isHCP ? 'HouseCallPro' : 'ServiceTitan'} and update customer contact preferences.</p>
                    </div>
                  }
                  title="Contact Preference"
                  trigger="hover"
                >
                  <InfoCircleOutlined style={{ color: '#1890ff', cursor: 'pointer' }} />
                </Popover>
              </div>
              <div className="flex items-center gap-3">
                <Switch
                  checked={smsSettings.contactUnchecked}
                  onChange={(checked) => {
                    setSmsSettings(prev => ({...prev, contactUnchecked: checked}));
                    setIsDirty(true);
                  }}
                />
              </div>
            </div>

            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-2">
                <label className="text-base text-black font-semibold">
                  🏷️ Customer Tagging
                </label>
                <Popover
                  content={<div style={{ maxWidth: '280px' }}>Would you like to automatically tag customers who opt out of SMS consent?</div>}
                  title="Customer Tagging"
                  trigger="hover"
                >
                  <InfoCircleOutlined style={{ color: '#1890ff', cursor: 'pointer' }} />
                </Popover>
              </div>
              <div className="flex items-center gap-3">
                <Switch
                  checked={smsSettings.addTag}
                  onChange={(checked) => {
                    setSmsSettings(prev => ({...prev, addTag: checked}));
                    setIsDirty(true);
                  }}
                />
              </div>

              <CustomCssTransition
                in={smsSettings.addTag === true}
                timeout={300}
                classNames="glimpse"
                unmountOnExit
              >
                <div className="flex flex-col gap-2">
                  <label className="text-sm text-gray-700 font-medium">
                    Select {isHCP ? 'HouseCallPro' : 'ServiceTitan'} Tags
                  </label>
                  <div className={`bg-white p-3 border rounded-lg ${
                    validationErrors.tagIds ? 'border-red-500' : 'border-gray-200'
                  }`}>
                    <ProvidersTags 
                      tagIds={smsSettings.tagIds}
                      onTagChange={(value) => {
                        setSmsSettings(prev => ({...prev, tagIds: [...value]}));
                        setIsDirty(true);
                        if (validationErrors.tagIds) {
                          setValidationErrors(prev => ({...prev, tagIds: false}));
                        }
                      }}
                      loading={loading}
                    />
                  </div>
                  {validationErrors.tagIds ? (
                    <p className="text-sm text-red-500">Please select at least one tag</p>
                  ) : (
                    <p className="text-xs text-gray-500">
                      These tags will be applied to customers who opt out of SMS consent
                    </p>
                  )}
                </div>
              </CustomCssTransition>
            </div>
          </div>
        </div>
      </CustomCssTransition>

      <CustomCssTransition
        in={smsSettings.compliance}
        timeout={300}
        classNames="glimpse"
        unmountOnExit
      >
        <div className="bg-white shadow-md rounded-lg p-6 mb-6">
          <div className="mb-4 flex items-center gap-2">
            <label className="text-lg text-black font-semibold">
              📄 Disclaimer Only
            </label>
            <Popover
              content={
                <div style={{ maxWidth: '280px' }}>
                  <p><strong>Client Info Additional Note</strong> is used as disclaimer text when SMS compliance is set to "Disclaimer Only" mode.</p>
                  <br/>
                  <p>Only administrators can edit this content. Regular users can view it but cannot make changes.</p>
                </div>
              }
              title="Disclaimer Info"
              trigger="hover"
            >
              <InfoCircleOutlined style={{ color: '#1890ff', cursor: 'pointer' }} />
            </Popover>
          </div>

          <div className="flex flex-col gap-4">
            <label className="text-base text-black font-semibold">
              Client Info Additional Note {!isAdmin && "(Read Only)"}
            </label>
            <Editor 
              value={editorContent} 
              onChange={(value) => {
                if (isAdmin) {
                  setEditorContent(value);
                  setIsDirty(true);
                }
              }} 
              readOnly={!isAdmin} 
            />
            {!isAdmin && (
              <p className="text-sm text-gray-500">
                Only administrators can edit this content
              </p>
            )}
          </div>
        </div>
      </CustomCssTransition>

      <div className="bg-white shadow-md rounded-lg p-6 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <label className="text-base text-black font-semibold">
            Enhance Leads
          </label>
          <Popover
            content={
              <div style={{ maxWidth: "280px" }}>
                <p>
                  When enabled, the scheduler sends the customer&apos;s email and
                  phone to the parent website so it can enhance the lead (for
                  example Google Ads enhanced conversions).
                </p>
              </div>
            }
            title="Enhance Leads"
            trigger="hover"
          >
            <InfoCircleOutlined style={{ color: "#1890ff", cursor: "pointer" }} />
          </Popover>
        </div>
        <div className="flex items-center gap-3">
          <Switch
            checked={enhanceLeads}
            onChange={(checked) => {
              setEnhanceLeads(checked);
              setIsDirty(true);
            }}
          />
          <span className="text-sm text-gray-500">
            {enhanceLeads
              ? "Customer email and phone will be sent to the parent page"
              : "Lead enhancement is off for this organization"}
          </span>
        </div>
      </div>

      <SaveButton onSubmit={handleSubmit} isDirty={isDirty} />
    </div>
  );
}
